import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Order"` — one order, in full. P12-03.
 *
 * Three cards, which are the design's three: **Who called**, **The order**, and **What
 * happened**. Plus the actions along the top — advance, cancel, undo, mark paid, print a ticket —
 * and the links back to the order book and to the conversation this order came out of.
 *
 * AMENDED 2026-09-25 (customer-portal review #112, #123, #133):
 *
 *   · **The links back reach the agent.** "Back to the order book" and "Open the call" went to
 *     `/agents/?…` with no agent id when the URL did not carry one. The order now names the
 *     VoiceAgent that took it (`voice_agent_id`, from its conversation), and the URL's own
 *     `?voiceAgent=` wins when present.
 *   · **"What happened" names every event** — placed, sent to the kitchen, cancelled and why,
 *     undone, marked paid, ticket printed — never a bare time.
 *   · **Cancel, with a reason, at any status that is not final**; and a **one-step undo** of the
 *     latest move for a wrong tap, for as long as the server offers it.
 *   · **An unpaid online order warns before it is fulfilled.** (AMENDED 2026-09-26 by plan-005
 *     OP-36, below: the #111 interim's "WeTalk sends no payment link" is gone.)
 *   · **"Print ticket" prints.** It records the print and opens the browser's print dialog on the
 *     ticket the API rendered; everything else on the page is hidden from the printout.
 *   · **Members and Viewers** see the order without the caller's number and address, and are
 *     told why.
 *
 * AMENDED 2026-09-26 (plan-005 OP-36; FR-PAY-10, FR-PAY-11, FR-PAY-12, OD-P5, OD-P6 (a)):
 *
 *   · **Review #111's interim is undone.** WeTalk sends payment links now, so an unpaid online
 *     order is "Awaiting online payment" (with the link's expiry) or "Online payment not
 *     completed" — never "no payment link was sent". The Payment card (`order-payment-panel.tsx`)
 *     shows every IC-56 state and carries the acts: resend, cancel the link, take payment on
 *     hand-over, refund (Owner/Admin).
 *   · **Starting an unpaid online order still warns** (`startingWarnsUnpaidOnline`), and the
 *     warning offers "Mark paid and continue" only when marking paid is possible.
 *   · **"Mark paid" is not offered while a link is live** (`markPaidIsAvailable`); "Cancel link"
 *     is, and says why. A paid online order is never offered "Mark paid".
 *   · **Cancelling a paid online order asks whether to refund** it (FR-PAY-12), and only an Owner
 *     or Admin may answer yes.
 *   · **Live**: the page re-reads on the shop's stream, and once more when an awaited link expires.
 *   · **The status pill** (design sync 2026-10-09): the state's own tint (`OrderStatusPill`), the cards the console's
 *     card finish.
 *
 * WHAT THE DESIGN ASKS FOR THAT IS NOT DRAWN HERE, AND WHY.
 *
 *   · **"Call back"** — the 2026-10-09 export dropped it too. The caller's number is shown as text a phone can
 *     dial.
 *   · **"From the call" — the quoted turns.** Quoting three lines of the transcript here would
 *     be this screen guessing which three matter. The link to the conversation lands on the
 *     History tab with the recording and the whole transcript.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Dialog, Icon, Radio, Textarea } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { formatMoney } from "../../money.js";
import { useGateway } from "../../api/provider.js";
import { useMemberCan } from "../../shell/member-capability.js";
import {
  customerOrderIsRefundable,
  markPaidIsAvailable,
  mayRefundCustomerOrder,
  startingWarnsUnpaidOnline,
} from "../../api/slice/customer-order-payment.js";
import {
  CUSTOMER_ORDER_STATUS_LABEL,
  type CustomerOrderDetailView,
  type CustomerOrderStatusView,
} from "../agents/[id]/orders/order-book-gateway.js";
import { useShopCustomerOrderStream } from "../agents/[id]/orders/order-book-live.js";
import { ORDER_BOOK_KEY, retryOrderBookRead } from "../agents/[id]/orders/orders-tab.js";
import { CustomerOrderPaymentPanel, REFUND_REFUSAL_TEXT } from "./order-payment-panel.js";
import "../agents/[id]/orders/orders.css";
import { OrderStatusPill } from "./order-status-pill.js";
import "./order-screen.css";

const DETAIL_KEY = (reference: string) => ["order-book-detail", reference] as const;

/** What the page says about an unpaid online order that is not finished (FR-PAY-10). */
function unpaidOnlineNoteOf(one: CustomerOrderDetailView): string | null {
  switch (one.paymentState) {
    case "awaiting_online_payment": {
      const until = one.payment?.expiresAtText ?? null;
      return (
        portalText("finance", "m0049", { value1: until === null ? "" : portalText("finance", "order.linkUntil", { until }) })
      );
    }
    case "online_payment_not_completed":
      return (
        portalText("finance", "m0050")
      );
    case "amount_mismatch":
      return portalText("finance", "m0051");
    default:
      return null;
  }
}

/** What the one advance button says. It names the next step, never "Next". */
const ADVANCE_LABEL: Readonly<Record<CustomerOrderStatusView, string | null>> = {
  get received() { return portalText("finance", "m1134"); },
  get preparing() { return portalText("finance", "m1135"); },
  get ready() { return portalText("finance", "m1136"); },
  get out_for_delivery() { return portalText("finance", "m1137"); },
  delivered: null,
  cancelled: null,
};

/**
 * The advance button's glyph, as the prototype draws it (design audit 2026-10-15): the fire for "Send to the
 * kitchen", the moped for "Mark out for delivery", the tick for "Mark delivered". "It is ready" is a step the
 * prototype's flow does not have, so it keeps the plain arrow.
 */
const ADVANCE_GLYPH = {
  received: GLYPH.fire,
  preparing: GLYPH.arrowRight,
  ready: GLYPH.moped,
  out_for_delivery: GLYPH.checkCircle,
  delivered: GLYPH.arrowRight,
  cancelled: GLYPH.arrowRight,
} as const satisfies Record<CustomerOrderStatusView, unknown>;

/** The steps that are "fulfilling" an order: cooking it, and handing it over. */
function advanceFulfils(status: CustomerOrderStatusView): boolean {
  return status === "received" || status === "ready" || status === "out_for_delivery";
}

/** The search every `/agents/$voiceAgentId` link carries; only `tab` and `conversation` vary. */
function voiceAgentSearchOf(tab: "orders" | "history", conversation: string) {
  return {
    tab,
    range: tab === "orders" ? ("7d" as const) : ("30d" as const),
    outcome: "all" as const,
    q: "",
    conversation,
    analyticsRange: "14d" as const,
    agentVersion: "",
    concern: "gaps" as const,
  };
}

export interface CustomerOrderScreenProps {
  readonly customerOrderReference: string;
  /** The agent the URL says we came from, or `""`; the order's own VoiceAgent otherwise. */
  readonly voiceAgentId: string;
}

export function CustomerOrderScreen(props: CustomerOrderScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<string | null>(null);
  /** Bumped on every print, so printing the same ticket twice opens the dialog twice. */
  const [printRequest, setPrintRequest] = useState(0);
  const [refusal, setRefusal] = usePortalMessage();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  /** FR-PAY-12: a paid online order's cancellation says whether its money goes back. */
  const [refundOnCancel, setRefundOnCancel] = useState<boolean | null>(null);
  const [unpaidWarning, setUnpaidWarning] = useState(false);
  const { role } = useMemberCan();

  const customerOrder = useQuery({
    queryKey: DETAIL_KEY(props.customerOrderReference),
    queryFn: () => gateway.getCustomerOrder(props.customerOrderReference),
  });

  // FR-PAY-11: the order's shop stream, reached through its VoiceAgent's summary (the order wire
  // does not carry its shop). The summary is the Orders tab's own query, so it is usually cached.
  const knownVoiceAgentId =
    props.voiceAgentId !== "" ? props.voiceAgentId : (customerOrder.data?.voiceAgentId ?? null);
  const summary = useQuery({
    queryKey: ORDER_BOOK_KEY.summary(knownVoiceAgentId ?? ""),
    queryFn: () => gateway.getCustomerOrderSummary(knownVoiceAgentId ?? ""),
    enabled: knownVoiceAgentId !== null,
    retry: retryOrderBookRead,
  });
  useShopCustomerOrderStream(summary.data?.shopId ?? null);

  // An awaited link that runs out moves the order to "not completed" with no event of its own on
  // every path, so the page re-reads once, just after the expiry it is showing.
  const expiresAtMs = customerOrder.data?.payment?.expiresAt?.getTime() ?? null;
  useEffect(() => {
    if (expiresAtMs === null) return undefined;
    const wait = Math.max(0, expiresAtMs - Date.now()) + 1_000;
    const timer = globalThis.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: DETAIL_KEY(props.customerOrderReference) });
    }, wait);
    return () => {
      globalThis.clearTimeout(timer);
    };
  }, [expiresAtMs, queryClient, props.customerOrderReference]);

  const settle = {
    onSuccess: (moved: CustomerOrderDetailView) => {
      setRefusal(null);
      queryClient.setQueryData(DETAIL_KEY(props.customerOrderReference), moved);
      // The Orders tab's figures and rows move with it.
      void queryClient.invalidateQueries({ queryKey: ["order-book-summary"] });
      void queryClient.invalidateQueries({ queryKey: ["order-book-page"] });
    },
    onError: (raised: unknown) => {
      // The server's own refusal, shown as the server's own words. A 409 here usually means
      // somebody else moved it — the counter tablet — and the answer is to say so and refetch,
      // never to retry with the status this screen last saw.
      setRefusal(() => (raised instanceof Error ? raised.message : portalText("finance", "m0001")));
      void queryClient.invalidateQueries({ queryKey: DETAIL_KEY(props.customerOrderReference) });
    },
  };

  const advance = useMutation({
    mutationFn: () => gateway.advanceCustomerOrder(props.customerOrderReference),
    ...settle,
  });
  const markPaid = useMutation({
    mutationFn: () => gateway.markCustomerOrderPaid(props.customerOrderReference),
    ...settle,
  });
  const markPaidThenAdvance = useMutation({
    mutationFn: async () => {
      await gateway.markCustomerOrderPaid(props.customerOrderReference);
      return gateway.advanceCustomerOrder(props.customerOrderReference);
    },
    ...settle,
  });
  const cancel = useMutation({
    mutationFn: (input: { readonly why: string; readonly refundPayment: boolean | null }) =>
      gateway.cancelCustomerOrder(props.customerOrderReference, input.why, input.refundPayment),
    ...settle,
    onSuccess: (moved: CustomerOrderDetailView) => {
      setCancelling(false);
      setReason("");
      setRefundOnCancel(null);
      settle.onSuccess(moved);
    },
  });
  const undo = useMutation({
    mutationFn: () => gateway.undoCustomerOrderMove(props.customerOrderReference),
    ...settle,
  });

  const print = useMutation({
    mutationFn: () => gateway.printOrderTicket(props.customerOrderReference),
    onSuccess: (printed) => {
      setTicket(printed.plainText);
      setPrintRequest((count) => count + 1);
      void queryClient.invalidateQueries({ queryKey: DETAIL_KEY(props.customerOrderReference) });
    },
  });

  // Review #123: "Print ticket" opens the printable ticket, once it is on the page.
  useEffect(() => {
    if (printRequest === 0) return;
    if (typeof globalThis.print === "function") globalThis.print();
  }, [printRequest]);

  if (customerOrder.data === undefined) {
    return <p className="c-note">{portalText("finance", "m0052")}</p>;
  }
  const one: CustomerOrderDetailView = customerOrder.data;
  const advanceLabel = ADVANCE_LABEL[one.status];
  const final = one.status === "delivered" || one.status === "cancelled";
  const busy =
    advance.isPending || markPaid.isPending || markPaidThenAdvance.isPending || cancel.isPending || undo.isPending;
  // The URL's agent when it names one, else the VoiceAgent that took the order (review #112).
  const voiceAgentId = props.voiceAgentId !== "" ? props.voiceAgentId : one.voiceAgentId;
  const warnsUnpaid = startingWarnsUnpaidOnline(one.paymentState);
  const offersMarkPaid = !one.paid && one.status !== "cancelled" && markPaidIsAvailable(one.paymentState);
  const unpaidNote = final ? null : unpaidOnlineNoteOf(one);
  // FR-PAY-12: cancelling an order whose money arrived online asks what happens to the money.
  const asksRefund = one.payment !== null && customerOrderIsRefundable(one.payment);
  const mayRefund = mayRefundCustomerOrder(role);
  const refundDecision: boolean | null = asksRefund ? (mayRefund ? refundOnCancel : false) : null;

  return (
    <section className="c-page">
      {voiceAgentId === null ? (
        <Link to="/agents" className="c-backlink">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />{portalText("finance", "m0053")}</Link>
      ) : (
        <Link
          to="/agents/$voiceAgentId"
          params={{ voiceAgentId }}
          search={voiceAgentSearchOf("orders", "")}
          className="c-backlink"
        >
          <Icon glyph={GLYPH.arrowLeft} size="sm" />{portalText("finance", "m0054")}</Link>
      )}

      <div className="c-headline-row">
        <div>
          {/* The prototype's "<when> · taken by <agent> on <medium>" (design audit 2026-10-15). The order view
              carries the VoiceAgent but not its conversation's medium, so "on a phone call" is not said; an order
              taken at the counter or through the API names its fulfilment instead. Review 120's "which agent took
              the order" is answered here. */}
          <p className="c-kicker">
            {one.voiceAgentName === null
              ? `${one.placedAtText} · ${one.fulfilmentText}`
              : portalText("finance", "value1_taken_by_value2_b67d43e4", { value1: one.placedAtText, value2: one.voiceAgentName })}
          </p>
          <h1 className="c-agent-title">
            {one.reference}
            <OrderStatusPill status={one.status} />
          </h1>
        </div>
        <div className="c-row c-orders-actions">
          {advanceLabel === null ? null : (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                if (warnsUnpaid && advanceFulfils(one.status)) setUnpaidWarning(true);
                else advance.mutate();
              }}
            >
              <Icon glyph={ADVANCE_GLYPH[one.status]} size="md" />
              {advanceLabel}
            </Button>
          )}
          {one.undo === null ? null : (
            <Button
              variant="secondary"
              disabled={busy}
              title={portalText("finance", "m0055", { value1: CUSTOMER_ORDER_STATUS_LABEL[one.undo.toStatus], value2: one.undo.untilText })}
              onClick={() => {
                undo.mutate();
              }}
            >
              <Icon glyph={GLYPH.arrowUDownLeft} size="md" />{portalText("finance", "m0056")}{CUSTOMER_ORDER_STATUS_LABEL[one.undo.fromStatus]}”
            </Button>
          )}
          {/* FR-PAY-10: never while a payment link is live, never once paid online. */}
          {!offersMarkPaid ? null : (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                markPaid.mutate();
              }}
            >
              <Icon glyph={GLYPH.creditCard} size="md" />{portalText("finance", "m0057")}</Button>
          )}
          <Button
            variant="secondary"
            disabled={print.isPending}
            onClick={() => {
              print.mutate();
            }}
          >
            <Icon glyph={GLYPH.printer} size="md" />{portalText("finance", "m0058")}</Button>
          {final ? null : (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setCancelling(true);
              }}
            >
              <Icon glyph={GLYPH.xCircle} size="md" />{portalText("finance", "m0059")}</Button>
          )}
        </div>
      </div>

      {unpaidNote === null ? null : (
        <p
          className={one.paymentState === "awaiting_online_payment" ? "c-orders-notice" : "c-orders-refusal"}
          role="note"
        >
          <Icon glyph={GLYPH.warningCircle} size="md" />
          <span>{unpaidNote}</span>
        </p>
      )}

      {refusal === null ? null : (
        <p className="c-orders-refusal" role="alert">
          <Icon glyph={GLYPH.warningCircle} size="md" />
          <span>
            {refusal}{portalText("finance", "m0060")}</span>
        </p>
      )}

      <div className="c-orders-detail">
        <div className="c-card">
          <p className="c-card-kicker">{portalText("finance", "m0061")}</p>
          <p className="c-orders-who">{one.partyName ?? portalText("finance", "m0062")}</p>
          <p className="c-orders-phone">
            {one.callerDetailWithheld ? portalText("finance", "m0063") : (one.partyPhone ?? portalText("finance", "m0064"))}
          </p>
          <dl className="c-orders-dl">
            <dt>{portalText("finance", "m0065")}</dt>
            <dd>{one.fulfilmentText}</dd>
            {one.address === null ? null : (
              <>
                <dt>{portalText("finance", "m0066")}</dt>
                <dd>{one.address}</dd>
              </>
            )}
            {one.addressNote === null ? null : (
              <>
                <dt>{portalText("finance", "m0067")}</dt>
                <dd>{one.addressNote}</dd>
              </>
            )}
            <dt>{portalText("finance", "m0068")}</dt>
            <dd>{String(one.etaMinute)}{portalText("finance", "m0069")}</dd>
            <dt>{portalText("finance", "m0008")}</dt>
            <dd>{one.paymentText}</dd>
          </dl>
          {one.callerDetailWithheld ? (
            <p className="c-orders-sub">{portalText("finance", "m0071")}</p>
          ) : null}
          <div className="c-orders-callback">
            {one.conversationId === null ? (
              <p className="c-orders-sub">{portalText("finance", "m0072")}</p>
            ) : voiceAgentId === null ? null : (
              <Button
                variant="ghost"
                onClick={() => {
                  void navigate({
                    to: "/agents/$voiceAgentId",
                    params: { voiceAgentId },
                    search: voiceAgentSearchOf("history", one.conversationId ?? ""),
                  });
                }}
              >{portalText("finance", "m0073")}</Button>
            )}
          </div>
        </div>

        <div className="c-card">
          <p className="c-card-kicker">{portalText("finance", "m0074")}</p>
          <div className="c-orders-lines">
            {one.line.map((line, index) => (
              <div className="c-orders-line" key={`${line.label}-${String(index)}`}>
                <span className="c-orders-line-qty">{String(line.quantity)}</span>
                <span className="c-orders-line-body">
                  <span className="c-orders-line-name">{line.label}</span>
                  {line.modification === "" ? null : <span className="c-orders-sub">{line.modification}</span>}
                  {line.notes === null ? null : <span className="c-orders-line-note">{line.notes}</span>}
                </span>
                <span className="c-orders-line-price">{formatMoney(line.lineTotal)}</span>
              </div>
            ))}
          </div>
          <dl className="c-orders-totals">
            <dt>{portalText("finance", "m0075")}</dt>
            <dd>{formatMoney(one.subtotal)}</dd>
            {one.discount.amountMinor === 0 ? null : (
              <>
                <dt>{portalText("finance", "m0076")}{one.couponCode === null ? "" : ` · ${one.couponCode}`}</dt>
                <dd>−{formatMoney(one.discount)}</dd>
              </>
            )}
            {one.deliveryFee.amountMinor === 0 ? null : (
              <>
                <dt>{portalText("finance", "m0077")}</dt>
                <dd>{formatMoney(one.deliveryFee)}</dd>
              </>
            )}
            <dt className="c-orders-total-key">{portalText("finance", "m0078")}</dt>
            <dd className="c-orders-total-value">{formatMoney(one.total)}</dd>
          </dl>
          {one.note === null ? null : <p className="c-orders-note">{one.note}</p>}
        </div>

        <CustomerOrderPaymentPanel one={one} role={role} busy={busy} />

        <div className="c-card">
          <p className="c-card-kicker">{portalText("finance", "m0079")}</p>
          <ol className="c-orders-timeline">
            {one.timeline.map((entry, index) => (
              <li className="c-orders-timeline-row" key={`${entry.atText}-${String(index)}`}>
                <span className="c-orders-sub">{entry.atText}</span>
                <span>{entry.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {ticket === null ? null : (
        <div className="c-orders-print" aria-label={portalText("finance", "m0080")}>
          <p className="c-card-kicker">{portalText("finance", "m0081")}</p>
          {/*
            The 42-column rendering the API returns, shown as-is and sent to the printer as-is:
            the print stylesheet hides everything else on the page. A shop with a thermal
            printer sends these bytes; a shop without one prints this.
          */}
          <pre className="c-orders-ticket">{ticket}</pre>
          <Button
            variant="secondary"
            onClick={() => {
              setPrintRequest((count) => count + 1);
            }}
          >
            <Icon glyph={GLYPH.printer} size="md" />{portalText("finance", "m0082")}</Button>
        </div>
      )}

      <Dialog
        open={cancelling}
        title={portalText("finance", "m0083", { value1: one.reference })}
        onClose={() => {
          setCancelling(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCancelling(false);
              }}
            >{portalText("finance", "m0084")}</Button>
            <Button
              variant="primary"
              disabled={reason.trim() === "" || (asksRefund && refundDecision === null) || cancel.isPending}
              onClick={() => {
                cancel.mutate({ why: reason.trim(), refundPayment: refundDecision });
              }}
            >{portalText("finance", "m0085")}</Button>
          </>
        }
      >
        <label className="c-orders-reason">
          <span>{portalText("finance", "m0086")}</span>
          <Textarea
            value={reason}
            maxLength={500}
            rows={3}
            placeholder={portalText("finance", "m0087")}
            onChange={(event) => {
              setReason(event.currentTarget.value);
            }}
          />
        </label>
        {!asksRefund || one.payment === null ? null : mayRefund ? (
          <div className="c-orders-choice" role="radiogroup" aria-label={portalText("finance", "m0088")}>
            <span>{portalText("finance", "m0089")}{formatMoney(one.payment.refundable)}{portalText("finance", "m0090")}</span>
            <Radio
              name="refund-on-cancel"
              label={portalText("finance", "m0091", { value1: formatMoney(one.payment.refundable) })}
              checked={refundOnCancel === true}
              onChange={() => {
                setRefundOnCancel(true);
              }}
            />
            <Radio
              name="refund-on-cancel"
              label={portalText("finance", "m0092")}
              checked={refundOnCancel === false}
              onChange={() => {
                setRefundOnCancel(false);
              }}
            />
          </div>
        ) : (
          <p className="c-orders-sub">{portalText("finance", "m0093")}{REFUND_REFUSAL_TEXT}
          </p>
        )}
        <p className="c-orders-sub">{portalText("finance", "m0094")}</p>
      </Dialog>

      <Dialog
        open={unpaidWarning}
        title={portalText("finance", "m0095")}
        onClose={() => {
          setUnpaidWarning(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setUnpaidWarning(false);
              }}
            >{portalText("finance", "m0096")}</Button>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setUnpaidWarning(false);
                advance.mutate();
              }}
            >{portalText("finance", "m0097")}</Button>
            {/* Only when marking paid is possible: never while the payment link is still live. */}
            {!offersMarkPaid ? null : (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => {
                  setUnpaidWarning(false);
                  markPaidThenAdvance.mutate();
                }}
              >{portalText("finance", "m0098")}</Button>
            )}
          </>
        }
      >
        <p>
          {one.paymentState === "awaiting_online_payment"
            ? portalText("finance", "m0099")
            : one.paymentState === "amount_mismatch"
              ? portalText("finance", "m0100")
              : portalText("finance", "m0101")}
        </p>
      </Dialog>
    </section>
  );
}
